import { useState, useCallback, useEffect } from "react";
import SectionContainer from "../../components/UI/SectionContainer";
import UserSearchBox from "../../components/UI/UserSearchBox";
import Pagination from "../../components/UI/Pagination";
import { openAlert, exportExcel, showPopup, USER_STATE } from '../../utils/common';
import Modal from '../../components/Layout/Modal';
import Spinner from '../../components/UI/Spinner';
import bluetoothService from '../../services/bluetoothService';

const BluetoothDataPage = () => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchKind, setSearchKind] = useState('0');
    const [keyword, setKeyword] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const pageSize = 10;
    const pageBlock = 5;

    const fetchList = useCallback(async (page = 1, skArg, kwArg) => {
      setLoading(true);
      try {
        const sk = skArg !== undefined ? Number(skArg) : 0; // default to 전체
        const kwState = kwArg !== undefined ? kwArg : ''; // default to empty keyword
        
        const result = await bluetoothService.getBluetoothDeviceList({
          page,
          pageSize,
          keyword: kwState,
          searchKind: sk
        });
        
        if (result.success) {
          setList(result.data?.devices || []);
          setTotalCount(result.data.pagination?.totalCount || 0);
        } else {
          openAlert({ title: '알림', description: result.message });
        }
      } catch (e) { 
        console.error(e); 
        openAlert({ title: '알림', description: '데이터 로드 중 오류가 발생했습니다.' });
      } finally {
        setLoading(false);
      }
    }, []);

  
    useEffect(() => {
      fetchList(currentPage, searchKind, keyword);
    }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = (directKeyword) => { 
      setCurrentPage(1); 
      // Use directKeyword if provided (from suggestion select), otherwise use keyword from state
      fetchList(1, searchKind, directKeyword !== undefined ? directKeyword : keyword); 
    };
    const handleReset = () => { 
      setSearchKind('0'); 
      setKeyword(''); 
      setCurrentPage(1); 
      // Call fetchList directly with reset values, consistent with new handleSearch signature
      fetchList(1, '0', ''); 
    };

    const handleExcelDownload = async () => {
        setLoading(true);
        try {
            const result = await bluetoothService.getBluetoothExcelData(keyword, Number(searchKind));
            
            if (result.success) {
                const excelConfig = {
                    excelFileName : "블루투스 데이터 수신.xlsx",
                    sheetName : "Sheet1"
                }
                exportExcel(result.data, excelConfig);
            } else {
                openAlert({ title: '알림', description: result.message });
            }
        } catch (e) {
            console.error(e);
            openAlert({ title: '알림', description: '엑셀 다운로드 중 오류가 발생했습니다.' });
        } finally {
            setLoading(false);
        }
    }

    const [deviceModalVisible, setDeviceModalVisible] = useState(false);
    const [deviceModal, setDeviceModal] = useState({
        manufacturerName: "-",
        modelNumber: "-",
        serialNumber: "-",
        firmwareVersion: "-",
        hardwareVersion: "-",
    }); 

    const showBluetoothDetail = async (idx) => {
        setLoading(true);
        try {
            const result = await bluetoothService.getBluetoothDeviceDetail(idx);
            
            if (result.success) {
                const value = result.data;
                setDeviceModal({
                    manufacturerName: value.manufacturerName || "-",
                    modelNumber: value.modelNumber || "-",
                    serialNumber: value.serialNumber || "-",
                    firmwareVersion: value.firmwareVersion || "-",
                    hardwareVersion: value.hardwareVersion || "-",
                });
                setDeviceModalVisible(true);
            } else {
                openAlert({ title: '알림', description: result.message });
            }
        } catch (e) {
            console.error(e);
            openAlert({ title: '알림', description: '블루투스 데이터 수신 상세 조회 중 오류가 발생했습니다.' });
        } finally {
            setLoading(false);
        }
    }

    const closeDeviceModal = () => {
        setDeviceModalVisible(false);
    }

    const showUserDetail = (idx) => {
        showPopup(`/user/profile/${idx}`, 'userinfo');
    }

    return (
        <SectionContainer title="블루투스 데이터 수신">
            <UserSearchBox
              value={keyword}
              userState={USER_STATE.NORMAL}
              onChange={setKeyword}
              searchKind={searchKind}
              onSelect={setSearchKind}
              onSearch={handleSearch}
              onReset={handleReset}
              onSuggestSelect={selectedVal => { 
                setKeyword(selectedVal); // Update the keyword state
                handleSearch(selectedVal); // And immediately search with this selected value
              }}
              totalCount={totalCount}
              showTotalCount={false}
              onExcelDownload={handleExcelDownload}
              customFetchSuggestions={bluetoothService.getSearchSuggest}
            />
            <div className="content_area">
              <Spinner visible={loading} />
              <div className="table_area">
                <div className="table">
                  <table className="bluetooth-data-table">
                    <thead>
                      <tr>
                        <th>성명</th>
                        <th>아이디</th>
                        <th>앱 정보</th>
                        <th>앱 버전</th>
                        <th>단말기 정보</th>
                        <th>업그레이드</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!loading && list.length === 0 ? (
                        <tr><td colSpan="6">블루투스 데이터 수신 내역이 존재하지 않습니다.</td></tr>
                      ) : (
                        list.map((item, idx) => (
                          <tr key={idx}>
                            <td className="clickable" onClick={() => showUserDetail(item.userIdx)} style={{cursor: 'pointer'}}>{item.userName}</td>
                            <td style={{cursor: 'default'}}>{item.userId}</td>  
                            <td style={{cursor: 'default'}}>{item.platform}</td>
                            <td style={{cursor: 'default'}}>{`ver ${item.appVersion}`}</td>
                            <td style={{cursor: 'default'}}>{item.connect === 1 ? <p className="contect-on un_line" onClick={() => {showBluetoothDetail(item.idx)}}>연결</p> : <p className="contect-off">미연결</p>}</td>
                            <td style={{cursor: 'default'}}>
                                <p>완료</p>
                                <p className="un_line">필요</p>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <Pagination currentPage={currentPage} totalCount={totalCount} pageSize={pageSize} pageBlock={pageBlock} onPageChange={setCurrentPage} />

            <Modal
                visible={deviceModalVisible}
                onClose={closeDeviceModal}
                title="단말기 정보"
                className="modal-sm"
            >
                <div className="terminal-info mt-2">
                    <dl>
                        <dt>기기명</dt>
                        <dd>{deviceModal.manufacturerName}</dd>
                    </dl>
                    <dl>
                        <dt>모델번호</dt>
                        <dd>{deviceModal.modelNumber}</dd>
                    </dl>
                    <dl>
                        <dt>시리얼번호</dt>
                        <dd>{deviceModal.serialNumber}</dd>
                    </dl>
                    <dl style={{alignItems: "center"}}>
                        <dt>Firmware version</dt>
                        <dd>{deviceModal.firmwareVersion}</dd>
                    </dl>
                    <dl style={{alignItems: "center"}}>
                        <dt>Hardware version</dt>
                        <dd>{deviceModal.hardwareVersion}</dd>
                    </dl>
                </div>
            </Modal>
        </SectionContainer>
    );
}

export default BluetoothDataPage; 